import { useState, useEffect } from 'react';
import { MoreHorizontal, Edit2, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Badge } from '@/components/ui/badge';

import { createDeadline } from '@/utils/todoHelpers';
import { loadTimeSettings, formatHour } from '@/utils/timeSettings';
import { loadDurationSettings, BASE_DURATION_OPTIONS } from '@/utils/durationSettings';
import { Mission, MissionSize } from '@/types/todo';
import { getGeneralMission } from '@/utils/missionHelpers';
import MissionSelectionModal from './MissionSelectionModal';
import MissionCreationModal from './MissionCreationModal';
import MissionModal from './MissionModal';


interface TodoDurationModalProps {
  isOpen: boolean;
  initialTitle: string;
  onConfirm: (title: string, durationMinutes?: number, missionId?: string) => void;
  onCancel?: () => void;
  onOpenSettings?: (defaultSection?: string) => void;
  missions?: Mission[];
  showMissions?: boolean;
  onCreateMission?: (name: string, size: MissionSize) => { id: string; mission: Mission };
}

// Get primary duration options from settings
const getPrimaryDurationOptions = () => {
  const settings = loadDurationSettings();
  return settings.primaryDurations.map(minutes => {
    const option = BASE_DURATION_OPTIONS.find(opt => opt.minutes === minutes);
    return option || { value: minutes.toString(), label: `${minutes} min`, minutes };
  });
};

interface DurationOption {
  value: string;
  label: string;
  minutes: number;
}

const isPastEndOfDay = (): boolean => {
  const timeSettings = loadTimeSettings();
  const now = new Date();
  const currentHour = now.getHours();
  // Return true if we're past end of day
  return currentHour >= timeSettings.endOfDayHour;
};

const getDynamicDurationOptions = (): DurationOption[] => {
  const timeSettings = loadTimeSettings();
  const now = new Date();
  const endOfDayTime = new Date();
  endOfDayTime.setHours(timeSettings.endOfDayHour, 0, 0, 0);
  
  // If end of day has already passed today, use tomorrow's end of day
  if (endOfDayTime <= now) {
    endOfDayTime.setDate(endOfDayTime.getDate() + 1);
  }
  
  const minutesToEndOfDay = Math.floor((endOfDayTime.getTime() - now.getTime()) / (1000 * 60));
  
  // Only include options that don't exceed end of day
  return BASE_DURATION_OPTIONS.filter(option => option.minutes <= minutesToEndOfDay);
};

export default function TodoDurationModal({ 
  isOpen, 
  initialTitle, 
  onConfirm, 
  onCancel, 
  onOpenSettings,
  missions = [],
  showMissions = false,
  onCreateMission
}: TodoDurationModalProps) {
  const [title, setTitle] = useState(initialTitle);
  const [duration, setDuration] = useState('30');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [selectedMissionId, setSelectedMissionId] = useState<string | undefined>(undefined);
  const [showMissionSelectionModal, setShowMissionSelectionModal] = useState(false);
  const [showMissionCreationModal, setShowMissionCreationModal] = useState(false);
  const [showMissionModal, setShowMissionModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Update title when initialTitle changes
  useEffect(() => {
    setTitle(initialTitle);
    setIsEditingTitle(false);
  }, [initialTitle]);

  // Reset showMoreOptions when modal opens, but auto-expand if needed
  useEffect(() => {
    if (isOpen) {
      const options = getDynamicDurationOptions();
      const pastEndOfDay = isPastEndOfDay();
      
      // Check if toggle group options are empty but chip options are available
      const primaryOpts = getPrimaryDurationOptions();
      const toggleGroupOptions = primaryOpts.filter(option => 
        options.some(d => d.value === option.value)
      );
      const hasToggleOptions = toggleGroupOptions.length > 0;
      const hasChipOptions = options.length > 0;
      
      // Auto-expand if no toggle options but chip options are available (and not in tomorrow mode)
      setShowMoreOptions(!pastEndOfDay && !hasToggleOptions && hasChipOptions);
    }
  }, [isOpen]);

  // Set default duration based on user settings
  useEffect(() => {
    if (isOpen) {
      const options = getDynamicDurationOptions();
      const settings = loadDurationSettings();
      const defaultMinutes = settings.defaultDuration;
      
      // Check if the default duration is available
      const defaultOption = options.find(opt => opt.minutes === defaultMinutes);
      
      // Priority: user's default -> 30m -> 15m -> largest available
      if (defaultOption) {
        setDuration(defaultOption.value);
      } else {
        const has30m = options.find(opt => opt.value === '30');
        const has15m = options.find(opt => opt.value === '15');
        
        if (has30m) {
          setDuration('30');
        } else if (has15m) {
          setDuration('15');
        } else if (options.length > 0) {
          // Find the largest available duration
          const largestOption = options.reduce((max, current) => 
            current.minutes > max.minutes ? current : max
          );
          setDuration(largestOption.value);
        } else {
          setDuration('30'); // fallback
        }
      }
    }
  }, [isOpen]);

  // Reset mission selection when modal opens for new task
  useEffect(() => {
    if (isOpen) {
      // Auto-assign to General mission if in mission mode and no mission selected
      if (showMissions && missions.length > 0) {
        const userMissions = missions.filter(m => !m.isGeneral && !m.isCompleted);
        if (userMissions.length > 0) {
          setSelectedMissionId(userMissions[0].id);
        } else {
          const generalMission = getGeneralMission(missions);
          if (generalMission) {
            setSelectedMissionId(generalMission.id);
          } else {
            setSelectedMissionId(undefined);
          }
        }
      } else {
        setSelectedMissionId(undefined);
      }
    }
  }, [isOpen, showMissions, missions]);

  const durationOptions = getDynamicDurationOptions();
  const primaryOptions = getPrimaryDurationOptions();
  const selectedDuration = durationOptions.find(d => d.value === duration);
  const defaultDeadline = createDeadline();
  const pastEndOfDay = isPastEndOfDay();
  const shouldShowTomorrowMode = pastEndOfDay;
  const noOptionsAvailable = durationOptions.length === 0 && !pastEndOfDay;
  
  // Compute schedule deadline for tomorrow mode
  const ts = loadTimeSettings();
  const scheduleDeadline = new Date();
  scheduleDeadline.setHours(ts.endOfDayHour, 0, 0, 0);
  if (shouldShowTomorrowMode) {
    scheduleDeadline.setDate(scheduleDeadline.getDate() + 1);
  }
  
  const handleContinue = () => {
    if (title.trim() && selectedDuration) {
      onConfirm(title.trim(), selectedDuration.minutes, selectedMissionId);
    }
  };

  const handleUseDefault = () => {
    const trimmed = title.trim();
    if (!trimmed) return;

    if (shouldShowTomorrowMode) {
      const minutes = Math.max(1, Math.ceil((scheduleDeadline.getTime() - Date.now()) / (1000 * 60)));
      onConfirm(trimmed, minutes, selectedMissionId);
    } else {
      onConfirm(trimmed, undefined, selectedMissionId);
    }
  };

  const handleMissionButtonClick = () => {
    if (missions.length === 0) {
      // No missions exist, directly open creation modal
      setShowMissionCreationModal(true);
    } else {
      // Missions exist, open selection modal
      setShowMissionSelectionModal(true);
    }
  };

  const handleMissionSelect = (missionId: string) => {
    setSelectedMissionId(missionId);
    setShowMissionSelectionModal(false);
  };

  const handleMissionCreate = (name: string, size: MissionSize) => {
    if (onCreateMission) {
      const result = onCreateMission(name, size);
      setSelectedMissionId(result.id);
    }
    setShowMissionCreationModal(false);
  };

  const selectedMission = missions.find(m => m.id === selectedMissionId);

  const handleMissionBadgeClick = () => {
    if (selectedMission) {
      setShowMissionModal(true);
    }
  };

  const handleChangeMission = () => {
    setShowMissionModal(false);
    handleMissionButtonClick();
  };

  const handleRemoveMission = () => {
    setSelectedMissionId(undefined);
    setShowMissionModal(false);
  };

  // Create mock todo for MissionModal
  const mockTodo = selectedMission ? {
    id: 'temp',
    title: title || 'New task',
    missionId: selectedMissionId,
    createdAt: new Date(),
    deadline: new Date(),
    completed: false
  } : undefined;



  return (
    <>
      <Dialog open={isOpen} onOpenChange={onCancel}>
        <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="space-y-6">
            {/* Title */}
            <DialogHeader className="text-left">
              <div className="flex items-center justify-between gap-2">
                {isEditingTitle ? (
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => setIsEditingTitle(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setIsEditingTitle(false);
                    }}
                    autoFocus
                    className="text-xl font-semibold"
                  />
                ) : (
                  <>
                    <DialogTitle className="flex-1">
                      {title || (shouldShowTomorrowMode ? "Schedule for tomorrow" : "New task")}
                    </DialogTitle>
                    <div className="flex items-center gap-1">
                       <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                       <DropdownMenuTrigger asChild>
                         <Button
                           variant="ghost"
                           size="sm"
                           className="text-muted-foreground shrink-0"
                           aria-label="More options"
                         >
                           <MoreHorizontal size={14} />
                         </Button>
                       </DropdownMenuTrigger>
                       <DropdownMenuContent align="end">
                         <DropdownMenuItem onClick={() => setIsEditingTitle(true)}>
                           <Edit2 className="mr-2 h-4 w-4" />
                           Edit
                         </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              setDropdownOpen(false);
                              setTimeout(() => onCancel?.(), 100);
                            }}
                            className="text-destructive"
                          >
                           <Trash2 className="mr-2 h-4 w-4" />
                           Delete
                         </DropdownMenuItem>
                       </DropdownMenuContent>
                     </DropdownMenu>
                     </div>
                   </>
                 )}
              </div>
              {shouldShowTomorrowMode ? (
                <DialogDescription>
                   It's past your end of day at <button 
                     onClick={() => onOpenSettings?.('time')}
                     className="underline underline-offset-4 cursor-pointer"
                   >
                     {formatHour(ts.endOfDayHour)}
                   </button>. This task will be scheduled for tomorrow.
                </DialogDescription>
              ) : durationOptions.length < BASE_DURATION_OPTIONS.length ? (
                <DialogDescription>
                   Your day ends at <button 
                     onClick={() => onOpenSettings?.('time')}
                     className="underline underline-offset-4 cursor-pointer"
                   >
                     {formatHour(ts.endOfDayHour)}
                   </button>
                </DialogDescription>
              ) : null}
              {showMissions && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant={!selectedMissionId ? "default" : "outline"}
                      className="cursor-pointer h-6 px-2.5 text-xs"
                      onClick={() => setSelectedMissionId(undefined)}
                    >
                      No mission
                    </Badge>
                    {missions.filter(m => !m.isCompleted).map((mission) => (
                      <Badge
                        key={mission.id}
                        variant={selectedMissionId === mission.id ? "default" : "outline"}
                        className="cursor-pointer h-6 px-2.5 text-xs"
                        onClick={() => setSelectedMissionId(mission.id)}
                      >
                        {mission.name}
                      </Badge>
                    ))}
                    <Badge
                      variant="outline"
                      className="cursor-pointer h-6 px-2.5 text-xs border-dashed"
                      onClick={() => setShowMissionCreationModal(true)}
                    >
                      + Create new
                    </Badge>
                  </div>
                </div>
              )}
            </DialogHeader>

            {/* Duration Selection - Hidden when in tomorrow mode or no options available */}
            {!shouldShowTomorrowMode && !noOptionsAvailable && (
              <div className="space-y-4 pb-4">
                {/* Header Row with "How long?" and "More.." button */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">How long?</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowMoreOptions(!showMoreOptions)}
                    className="text-muted-foreground hover:text-foreground text-sm"
                  >
                    More
                    {showMoreOptions ? (
                      <ChevronUp className="ml-1 h-4 w-4" />
                    ) : (
                      <ChevronDown className="ml-1 h-4 w-4" />
                    )}
                  </Button>
                </div>
                
                {/* Primary Duration Options - Toggle Group */}
                <div className="space-y-3">
                  <ToggleGroup
                    type="single"
                    value={duration}
                    onValueChange={(value) => value && setDuration(value)}
                    variant="outline"
                    className="grid grid-cols-4 gap-2"
                  >
                    {primaryOptions.filter(option => 
                      durationOptions.some(d => d.value === option.value)
                    ).map((option) => (
                      <ToggleGroupItem
                        key={option.value}
                        value={option.value}
                        size="default"
                        className="h-12"
                      >
                        {option.label}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>


                  {/* Expandable Full Options */}
                  {showMoreOptions && (
                    <div className="space-y-2 pt-2 border-t border-border">
                      {/* First row: 1m, 3m, 5m, 10m */}
                      <div className="flex gap-2 justify-start">
                        {durationOptions.slice(0, 4).map((option) => (
                          <Button
                            key={option.value}
                            variant={duration === option.value ? "default" : "outline"}
                            onClick={() => setDuration(option.value)}
                            size="sm"
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                      
                      {/* Second row: 15m, 20m, 25m, 30m */}
                      {durationOptions.length > 4 && (
                        <div className="flex gap-2 justify-start">
                          {durationOptions.slice(4, 8).map((option) => (
                            <Button
                              key={option.value}
                              variant={duration === option.value ? "default" : "outline"}
                              onClick={() => setDuration(option.value)}
                              size="sm"
                            >
                              {option.label}
                            </Button>
                          ))}
                        </div>
                      )}

                      {/* Third row: 45m, 1hr, 1.5hr, 2hr, 3hr */}
                      {durationOptions.length > 8 && (
                        <div className="flex gap-2 justify-start">
                          {durationOptions.slice(8).map((option) => (
                            <Button
                              key={option.value}
                              variant={duration === option.value ? "default" : "outline"}
                              onClick={() => setDuration(option.value)}
                              size="sm"
                            >
                              {option.label}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Continue button - Hidden when in tomorrow mode or no options available */}
              {!shouldShowTomorrowMode && !noOptionsAvailable && (
                <Button
                  onClick={handleContinue}
                  disabled={!title.trim()}
                  className="w-full h-12 text-base"
                >
                  Continue
                </Button>
              )}
              
              <Button
                variant="secondary"
                onClick={handleUseDefault}
                disabled={!title.trim()}
                className="w-full h-12 text-base"
              >
                {shouldShowTomorrowMode ? 'Schedule for tomorrow' : 'Set for end of day'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sibling Modals - Outside of main Dialog */}
      <MissionSelectionModal
        isOpen={showMissionSelectionModal}
        onClose={() => setShowMissionSelectionModal(false)}
        missions={missions}
        onSelectMission={handleMissionSelect}
        onCreateNew={() => setShowMissionCreationModal(true)}
      />
      
      <MissionCreationModal
        isOpen={showMissionCreationModal}
        onClose={() => setShowMissionCreationModal(false)}
        onCreateMission={handleMissionCreate}
      />
      
      <MissionModal 
        isOpen={showMissionModal}
        onClose={() => setShowMissionModal(false)}
        todo={mockTodo}
        missions={missions}
        onChangeMission={handleChangeMission}
        onRemoveMission={handleRemoveMission}
      />
    </>
  );
}